import { templateFoldersKeys } from "@/queries/forms/template-folder.query.ts";
import { apiSurveys } from "@/api/survey.api.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiFormTemplate,
  ApiFormTemplateDuplicatePayload,
  ApiFormTemplateType,
  ApiFormTemplateFilter,
  ApiFormTemplateMovePayload,
  ApiFormTemplateSavePayload,
} from "@/types/forms/form-template.types.ts";
import { Control, FieldValues } from "react-hook-form";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { FormProviderTypeSaveAsTemplate } from "@/components/forms/form-editor.types.ts";
const surveyTemplateKeys = createQueryKeys("surveyTemplates", {
  byId: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (!id) throw new ApiPassException("Id is required");
      const result = await apiSurveys.getSurveyTemplateById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  search: (filter: ApiFormTemplateFilter) => ({ queryKey: [filter] }),
});

export const useSurveyTemplateById = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...surveyTemplateKeys.byId(id),
    enabled: !!id && enabled,
  });
};

export const useSurveyTemplateSearch = (filter: ApiFormTemplateFilter, enabled: boolean = true) => {
  return useQuery({
    ...surveyTemplateKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiSurveys.searchSurveyTemplates({ ...filter }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });
};

export interface SurveyTemplateSaveParams {
  id?: number;
  payload: ApiFormTemplateSavePayload;
}

export const useSurveyTemplateSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: SurveyTemplateSaveParams) => {
      const result = id
        ? await apiSurveys.updateSurveyTemplate(id, payload)
        : await apiSurveys.createSurveyTemplate(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (template: ApiFormTemplate, { id }) => {
      queryClient.setQueryData(surveyTemplateKeys.byId(template.id).queryKey, template);
      queryClient.invalidateQueries({ queryKey: surveyTemplateKeys.search._def });
      if (!id)
        void queryClient.invalidateQueries({
          queryKey: templateFoldersKeys.all("surveys").queryKey,
        });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyTemplateDuplicate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({
      id,
      payload = {},
    }: {
      id: number;
      payload?: ApiFormTemplateDuplicatePayload;
    }) {
      const result = await apiSurveys.duplicateSurveyTemplate(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (template: ApiFormTemplate, { payload }) => {
      if (payload?.target_type === ApiFormTemplateType.performance_review) {
        queryClient.invalidateQueries({ queryKey: ["performanceReviewTemplates"] });
        void queryClient.invalidateQueries({
          queryKey: templateFoldersKeys.all("performance-reviews").queryKey,
        });
      } else {
        queryClient.setQueryData(surveyTemplateKeys.byId(template.id).queryKey, template);
        queryClient.invalidateQueries({ queryKey: surveyTemplateKeys.search._def });
        void queryClient.invalidateQueries({
          queryKey: templateFoldersKeys.all("surveys").queryKey,
        });
      }
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyTemplateDelete = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      const result = await apiSurveys.deleteSurveyTemplate(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: surveyTemplateKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: templateFoldersKeys.all("surveys").queryKey });
      queryClient.invalidateQueries({ queryKey: surveyTemplateKeys.byId(id).queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyTemplateCreateFromSurvey = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: FormProviderTypeSaveAsTemplate) => {
      const result = await apiSurveys.createSurveyTemplateFromSurvey(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (template: ApiFormTemplate) => {
      queryClient.setQueryData(surveyTemplateKeys.byId(template.id).queryKey, template);
      queryClient.invalidateQueries({ queryKey: surveyTemplateKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: templateFoldersKeys.all("surveys").queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSurveyTemplateMove = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiFormTemplateMovePayload }) => {
      const result = await apiSurveys.moveSurveyTemplate(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: surveyTemplateKeys._def }),
        queryClient.invalidateQueries({ queryKey: templateFoldersKeys.all("surveys").queryKey }),
      ]),
    onError: (error: Error) => onError(error),
  });
};
